'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  name: string;
  role: string;
  company: string;
  quote: string;
  image: string;
  alt: string;
  rating: number;
}

interface TestimonialsSectionProps {
  className?: string;
}

const TestimonialsSection = ({ className = '' }: TestimonialsSectionProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials: Testimonial[] = [
  {
    name: 'James Wilson',
    role: 'Professional Trader',
    company: 'Independent',
    quote: 'Signaly transformed my trading approach. The AI signals are incredibly accurate, and I\'ve seen a 45% increase in my portfolio since joining. No more guesswork—just data-driven decisions.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1da91affa-1763293647230.png",
    alt: 'Middle-aged man with gray hair in navy suit smiling confidently',
    rating: 5
  },
  {
    name: 'Maria Garcia',
    role: 'Investment Advisor',
    company: 'Wealth Management Group',
    quote: 'As a financial advisor, I recommend Signaly to all my clients. The transparency and reliability of their signals have helped my clients achieve consistent returns without the complexity.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18d4825d1-1763293841178.png",
    alt: 'Hispanic woman with dark hair in burgundy blazer with professional smile',
    rating: 5
  },
  {
    name: 'Robert Chen',
    role: 'Crypto Enthusiast',
    company: 'Tech Startup Founder',
    quote: 'I was skeptical about trading signals until I tried Signaly. The 87% confidence rate isn\'t just marketing—it\'s real. I\'ve been profitable for 8 consecutive months now.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_103702769-1763301708118.png",
    alt: 'Asian man with glasses in white shirt smiling warmly in bright office',
    rating: 5
  },
  {
    name: 'Amanda Foster',
    role: 'Trading Newcomer',
    company: 'Marketing Professional',
    quote: 'I had zero trading experience before Signaly. The platform made it so easy to start—no complicated courses or confusing jargon. Just clear signals that actually work.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15aff9691-1763301639861.png",
    alt: 'Young woman with blonde hair in light blue blouse with friendly expression',
    rating: 5
  }];


  const handlePrevious = () => {
    setCurrentIndex((prev) => prev === 0 ? testimonials.length - 1 : prev - 1);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => prev === testimonials.length - 1 ? 0 : prev + 1);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className={`py-16 lg:py-24 bg-card ${className}`}>
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-foreground mb-6">
            What Our Community Says
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Join thousands of traders who trust Signaly for their trading decisions.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-background border border-border rounded-lg p-8 lg:p-12 relative">
            <div className="absolute top-8 left-8 text-6xl text-primary/20 font-heading">
              "
            </div>
            
            <div className="flex flex-col lg:flex-row items-center gap-8 mb-8">
              <div className="relative w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
                <AppImage
                  src={currentTestimonial.image}
                  alt={currentTestimonial.alt}
                  className="w-full h-full object-cover" />

              </div>
              
              <div className="flex-1 text-center lg:text-left">
                <div className="flex justify-center lg:justify-start gap-1 mb-3">
                  {Array.from({ length: currentTestimonial.rating }).map((_, i) =>
                  <Icon key={i} name="StarIcon" size={20} className="text-warning" variant="solid" />
                  )}
                </div>
                <p className="text-lg text-muted-foreground leading-relaxed mb-6 italic">
                  {currentTestimonial.quote}
                </p>
                <div>
                  <div className="text-lg font-heading font-semibold text-foreground">
                    {currentTestimonial.name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {currentTestimonial.role} • {currentTestimonial.company}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center gap-4">
              <button
                onClick={handlePrevious}
                className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all duration-200"
                aria-label="Previous testimonial">

                <Icon name="ChevronLeftIcon" size={20} />
              </button>
              
              <div className="flex gap-2">
                {testimonials.map((_, index) =>
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  index === currentIndex ? 'bg-primary w-8' : 'bg-border'}`
                  }
                  aria-label={`Go to testimonial ${index + 1}`} />

                )}
              </div>
              
              <button
                onClick={handleNext}
                className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all duration-200"
                aria-label="Next testimonial">

                <Icon name="ChevronRightIcon" size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;